import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Input,
  Select,
  Table,
  Spin,
  notification,
  Tag,
} from "antd";
import { PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { fetchTeam, upsertTeamMember, deleteTeamMember } from "./service";
import styles from "./style.module.scss";

const ROLE_OPTIONS = [
  { value: "admin", labelKey: "sonic.role_admin" },
  { value: "editor", labelKey: "sonic.role_editor" },
  { value: "approver", labelKey: "sonic.role_approver" },
  { value: "viewer", labelKey: "sonic.role_viewer" },
];

const TeamTab = ({ active }) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchTeam();
    if (res && !res.error) setMembers(res.members || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const addMember = async () => {
    if (!email.trim()) return;
    setSaving(true);
    const res = await upsertTeamMember({ email: email.trim(), role });
    setSaving(false);
    if (res && !res.error) {
      notification.success({ message: t("sonic.team_member_saved") });
      setEmail("");
      load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const removeMember = async (id) => {
    const res = await deleteTeamMember(id);
    if (res && !res.error) {
      notification.success({ message: t("sonic.team_member_removed") });
      load();
    } else {
      notification.error({ message: (res && res.error) || t("sonic.action_failed") });
    }
  };

  const columns = [
    {
      title: t("sonic.team_email"),
      dataIndex: "email",
      key: "email",
    },
    {
      title: t("sonic.team_role"),
      dataIndex: "role",
      key: "role",
      render: (v) => (
        <Tag color={v === "admin" ? "purple" : "default"}>
          {t(`sonic.role_${v}`)}
        </Tag>
      ),
    },
    {
      title: "",
      key: "actions",
      render: (_, row) => (
        <Button type="link" danger size="small" onClick={() => removeMember(row.id)}>
          {t("sonic.remove")}
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className={styles.centeredSpin}>
        <Spin />
      </div>
    );
  }

  return (
    <div className={styles.tabInner}>
      <div className={styles.sonicPanel}>
        <div className={styles.teamHeader}>
          <TeamOutlined className={styles.teamHeaderIcon} />
          <div>
            <h3>{t("sonic.team_title")}</h3>
            <p className={styles.fieldHelper}>{t("sonic.team_desc")}</p>
          </div>
        </div>

        <div className={styles.teamAddRow}>
          <Input
            placeholder={t("sonic.team_email_ph")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ maxWidth: 280 }}
          />
          <Select
            value={role}
            onChange={setRole}
            style={{ width: 160 }}
            options={ROLE_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey),
            }))}
          />
          <Button type="primary" icon={<PlusOutlined />} loading={saving} onClick={addMember}>
            {t("sonic.team_add_member")}
          </Button>
        </div>

        <Table
          rowKey="id"
          dataSource={members}
          columns={columns}
          pagination={false}
          locale={{ emptyText: t("sonic.team_empty") }}
        />
      </div>
    </div>
  );
};

export default TeamTab;
